/** @file One analysis argument as a control, by kind: checklists, pickers, enums, numbers, text, a note. */
import type { Doc } from '@pose2d/format/types';
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import type { ReactNode } from 'react';
import { CheckList } from '../../ui-kit/CheckList';
import { NumberField } from '../../ui-kit/NumberField';
import { Select } from '../../ui-kit/Select';
import { TextInput } from '../../ui-kit/TextInput';
import { argOptions } from './argOptions';
import type { ArgField, ArgKind } from './autorigTypes';
import { frameDefault } from './frameDefault';

/** Props of ArgFieldView; `onChange(undefined)` leaves the argument out. */
export interface ArgFieldProps {
  t: Translate;
  field: ArgField;
  value: unknown;
  doc: Doc;
  onChange: (value: unknown) => void;
}

type Limb = Record<string, unknown> & { bone: string };

const names = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const limbsOf = (value: unknown): Limb[] => (Array.isArray(value) ? value.filter((l): l is Limb => typeof l === 'object' && l !== null && typeof (l as { bone?: unknown }).bone === 'string') : []);

/** A select over `choices` (value, shown label) with an "Automatic" / "(choose)" first entry meaning "not given". */
function pick(p: ArgFieldProps, first: CatalogKey, choices: readonly (readonly [string, string])[]): ReactNode {
  const options = [{ value: '', label: p.t(first) }, ...choices.map(([value, label]) => ({ value, label }))];
  return <Select label={p.t(p.field.label)} value={text(p.value)} options={options} onChange={(v) => { p.onChange(v === '' ? undefined : v); }} />;
}

const entity = (p: ArgFieldProps): ReactNode => pick(p, 'autorig.arg.choose', argOptions(p.doc, p.field).map((n) => [n, n] as const));

/** A fixed enum: `choices` pair each api value with the catalog key of its label. */
const enumOf = (choices: readonly (readonly [string, CatalogKey])[]) => (p: ArgFieldProps): ReactNode =>
  pick(p, 'autorig.arg.auto', choices.map(([value, key]) => [value, p.t(key)] as const));

/** A checklist over entity names; the last box unchecked leaves the argument out (the analysis default). */
const checklist = (p: ArgFieldProps): ReactNode => (
  <CheckList legend={p.t(p.field.label)} options={argOptions(p.doc, p.field)} picked={names(p.value)} onChange={(next) => { p.onChange(next.length === 0 ? undefined : next); }} />
);

/** Limbs are `{ bone, phase?, kind? }`: a checked bone keeps the phase and kind it already has. */
function limbs(p: ArgFieldProps): ReactNode {
  const kept = limbsOf(p.value);
  const pickLimbs = (next: string[]): void => { p.onChange(next.length === 0 ? undefined : next.map((bone) => kept.find((l) => l.bone === bone) ?? { bone })); };
  return <CheckList legend={p.t(p.field.label)} options={argOptions(p.doc, p.field)} picked={kept.map((l) => l.bone)} onChange={pickLimbs} />;
}

function whole(p: ArgFieldProps): ReactNode {
  const [min, max] = p.field.range ?? [0, 9999];
  const value = typeof p.value === 'number' ? p.value : frameDefault(p.doc, [min, max]);
  return <NumberField label={p.t(p.field.label)} value={value} min={min} max={max} step={1} precision={0} onCommit={p.onChange} />;
}

const VIEWS: Record<ArgKind, (p: ArgFieldProps) => ReactNode> = {
  images: checklist,
  bones: checklist,
  limbs,
  attachment: entity,
  bone: entity,
  facing: enumOf([['left', 'autorig.arg.left'], ['right', 'autorig.arg.right']]),
  walkFacing: enumOf([['right', 'autorig.arg.right'], ['left', 'autorig.arg.left'], ['front', 'autorig.arg.front']]),
  method: enumOf([['heat', 'autorig.arg.heat'], ['envelope', 'autorig.arg.envelope']]),
  detail: (p) => <NumberField label={p.t(p.field.label)} value={typeof p.value === 'number' ? p.value : 0.5} min={0} max={1} step={0.05} precision={2} onCommit={p.onChange} />,
  int: whole,
  text: (p) => <TextInput label={p.t(p.field.label)} value={text(p.value)} onCommit={(v) => { p.onChange(v === '' ? undefined : v); }} />,
  pairs: (p) => <p className="autorig-note">{p.t('autorig.arg.pairs', { n: Array.isArray(p.value) ? p.value.length : 0 })}</p>,
};

/** Dispatches on `field.kind` (exhaustive table). */
export function ArgFieldView(props: ArgFieldProps) {
  return <div className="autorig-arg">{VIEWS[props.field.kind](props)}</div>;
}
